<template>
  <div class="p-3 bg-gray-50 dark:bg-gray-900 min-h-screen">
    <!-- Encabezado -->
    <div class="flex flex-col md:flex-row justify-between items-start md:items-center gap-2 mb-4">
      <div>
        <h1 class="text-xl font-bold text-gray-800 dark:text-white">Salidas de Herramientas</h1>
        <p class="text-xs text-gray-600 dark:text-gray-400">Control de salida y devolución de herramientas del taller</p>
      </div>
      <div class="flex items-center gap-2">
        <button @click="$router.push('/salida-herramienta/nueva')" class="flex items-center px-3 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-all text-xs font-semibold shadow hover:shadow-md">
          <Icon icon="mdi:tools" class="mr-1.5" />
          <span>Nueva Salida</span>
        </button>
      </div>
    </div>

    <!-- Filtros -->
    <div class="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-3 mb-4 border border-gray-200 dark:border-gray-700">
      <div class="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
        <div>
          <label class="block text-[10px] font-bold text-gray-400 uppercase ml-1 mb-0.5">Estado</label>
          <select v-model="filterEstado" @change="handleFilterChange" class="w-full px-2 py-1.5 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-xs outline-none dark:text-white">
            <option value="all">Todos los estados</option>
            <option value="Pendiente">Pendiente</option>
            <option value="Devuelto">Devuelto</option>
            <option value="Atrasado">Atrasado</option>
          </select>
        </div>
        <div>
          <label class="block text-[10px] font-bold text-gray-400 uppercase ml-1 mb-0.5">Buscar Código/Obs</label>
          <div class="relative">
            <Icon icon="mdi:magnify" class="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input v-model="searchQuery" type="text" placeholder="Buscar..." class="w-full pl-9 pr-3 py-1.5 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-xs outline-none dark:text-white" />
          </div>
        </div>
        <div class="md:col-span-2 flex gap-2">
          <button @click="resetFilters" class="flex-1 py-1.5 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-lg text-xs font-semibold hover:bg-gray-300 dark:hover:bg-gray-600 transition-all">
            Limpiar Filtros
          </button>
        </div>
      </div>
    </div>

    <!-- Tabla de Salidas -->
    <div class="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
      <div v-if="loading" class="p-8 flex flex-col items-center justify-center">
        <Icon icon="svg-spinners:180-ring-with-bg" class="text-4xl text-indigo-600 mb-2" />
        <p class="text-sm text-gray-500">Cargando salidas...</p>
      </div>

      <div v-else-if="filteredSalidas.length === 0" class="p-12 text-center">
        <Icon icon="mdi:toolbox-outline" class="text-6xl text-gray-300 mb-4 mx-auto" />
        <h3 class="text-lg font-bold text-gray-400">No se encontraron salidas</h3>
        <p class="text-sm text-gray-500">No hay registros que coincidan con los filtros aplicados.</p>
      </div>

      <div v-else class="overflow-x-auto">
        <table class="w-full text-left border-collapse">
          <thead>
            <tr class="bg-gray-50 dark:bg-gray-700/50">
              <th class="px-4 py-3 text-[10px] font-bold text-gray-400 uppercase tracking-wider border-b border-gray-200 dark:border-gray-700">Código</th>
              <th class="px-4 py-3 text-[10px] font-bold text-gray-400 uppercase tracking-wider border-b border-gray-200 dark:border-gray-700">Fecha Salida</th>
              <th class="px-4 py-3 text-[10px] font-bold text-gray-400 uppercase tracking-wider border-b border-gray-200 dark:border-gray-700">Herramienta</th>
              <th class="px-4 py-3 text-[10px] font-bold text-gray-400 uppercase tracking-wider border-b border-gray-200 dark:border-gray-700">Cantidad</th>
              <th class="px-4 py-3 text-[10px] font-bold text-gray-400 uppercase tracking-wider border-b border-gray-200 dark:border-gray-700">Motivo</th>
              <th class="px-4 py-3 text-[10px] font-bold text-gray-400 uppercase tracking-wider border-b border-gray-200 dark:border-gray-700">Responsables</th>
              <th class="px-4 py-3 text-[10px] font-bold text-gray-400 uppercase tracking-wider border-b border-gray-200 dark:border-gray-700">Estado</th>
              <th class="px-4 py-3 text-[10px] font-bold text-gray-400 uppercase tracking-wider border-b border-gray-200 dark:border-gray-700">Acciones</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-100 dark:divide-gray-700">
            <tr v-for="salida in paginatedSalidas" :key="salida.idSalida" class="hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors">
              <td class="px-4 py-3">
                <div class="text-xs font-bold text-gray-800 dark:text-white">{{ salida.codigoSalida }}</div>
              </td>
              <td class="px-4 py-3">
                <div class="text-[11px] font-medium text-gray-800 dark:text-gray-200">{{ formatDate(salida.fechaSalida) }}</div>
                <div class="text-[9px] text-gray-400">{{ formatTime(salida.fechaSalida) }}</div>
              </td>
              <td class="px-4 py-3">
                <div class="text-[11px] font-bold text-indigo-600 dark:text-indigo-400">{{ salida.nombreHerramienta }}</div>
              </td>
              <td class="px-4 py-3 text-sm font-bold text-gray-700 dark:text-gray-300">
                {{ salida.cantidadSalida }}
              </td>
              <td class="px-4 py-3">
                <div class="text-[10px] text-gray-600 dark:text-gray-400 max-w-[150px] truncate" :title="salida.motivo">
                  {{ salida.motivo || '---' }}
                </div>
              </td>
              <td class="px-4 py-3">
                <div class="flex flex-col gap-1">
                  <div class="flex items-center gap-1">
                    <span class="text-[9px] font-bold text-gray-400 uppercase">Entrega:</span>
                    <span class="text-[10px] text-gray-700 dark:text-gray-300">{{ getUsuarioNombre(salida.idUsuarioEntrega) }}</span>
                  </div>
                  <div class="flex items-center gap-1">
                    <span class="text-[9px] font-bold text-gray-400 uppercase">Recibe:</span>
                    <span class="text-[10px] text-gray-700 dark:text-gray-300">{{ getUsuarioNombre(salida.idUsuarioRecibe) }}</span>
                  </div>
                </div>
              </td>
              <td class="px-4 py-3">
                <span :class="['px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-tight', getEstadoClass(salida.estado)]">
                  {{ salida.estado }}
                </span>
              </td>
              <td class="px-4 py-3 text-center">
                <div class="flex items-center gap-1 justify-center">
                  <button @click="viewDetails(salida)" class="p-1.5 text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg transition-colors" title="Ver Detalles">
                    <Icon icon="mdi:eye" />
                  </button>
                  <button v-if="salida.estado !== 'Devuelto'" @click="$router.push(`/devolucion-herramienta/nueva/${salida.idSalida}`)" class="p-1.5 text-green-500 hover:bg-green-50 dark:hover:bg-green-900/30 rounded-lg transition-colors" title="Devolver Herramienta">
                    <Icon icon="mdi:keyboard-return" />
                  </button>
                  <button @click="openEditModal(salida)" class="p-1.5 text-amber-500 hover:bg-amber-50 dark:hover:bg-amber-900/30 rounded-lg transition-colors" title="Editar">
                    <Icon icon="mdi:pencil" />
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Paginación -->
      <div v-if="filteredSalidas.length > itemsPerPage" class="px-4 py-3 bg-gray-50 dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 flex justify-between items-center">
        <span class="text-[10px] text-gray-500 font-bold uppercase">Total: {{ filteredSalidas.length }} registros</span>
        <div class="flex gap-1">
          <button @click="currentPage--" :disabled="currentPage === 1" class="p-1 border border-gray-300 dark:border-gray-600 rounded disabled:opacity-50 dark:text-white">
            <Icon icon="mdi:chevron-left" />
          </button>
          <span class="px-3 py-1 text-xs font-bold dark:text-white">{{ currentPage }} / {{ totalPages }}</span>
          <button @click="currentPage++" :disabled="currentPage === totalPages" class="p-1 border border-gray-300 dark:border-gray-600 rounded disabled:opacity-50 dark:text-white">
            <Icon icon="mdi:chevron-right" />
          </button>
        </div>
      </div>
    </div>

    <!-- Modal para Salida -->
    <Teleport to="body">
      <div v-if="showModal" class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm transition-all">
        <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden animate-fadeIn">
          <div class="p-4 border-b border-gray-100 dark:border-gray-700 flex justify-between items-center bg-indigo-600">
            <h2 class="text-white font-bold text-lg">{{ isEditing ? 'Editar Salida' : 'Registrar Nueva Salida' }}</h2>
            <button @click="closeModal" class="text-white hover:rotate-90 transition-transform">
              <Icon icon="mdi:close" class="text-xl" />
            </button>
          </div>
          
          <form @submit.prevent="saveSalida" class="p-6">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <!-- Código de Salida -->
              <div>
                <label class="block text-xs font-bold text-gray-500 uppercase mb-1">Código de Salida *</label>
                <input v-model="form.codigoSalida" type="text" required class="w-full px-3 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-sm focus:ring-2 focus:ring-indigo-500 outline-none dark:text-white" placeholder="SAL-001" />
              </div>

              <!-- Estado -->
              <div>
                <label class="block text-xs font-bold text-gray-500 uppercase mb-1">Estado *</label>
                <select v-model="form.estado" required class="w-full px-3 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-sm focus:ring-2 focus:ring-indigo-500 outline-none dark:text-white">
                  <option value="Pendiente">Pendiente</option>
                  <option value="Devuelto">Devuelto</option>
                  <option value="Atrasado">Atrasado</option>
                </select>
              </div>

              <!-- Herramienta -->
              <div class="md:col-span-2">
                <label class="block text-xs font-bold text-gray-500 uppercase mb-1">Herramienta *</label>
                <select v-model.number="form.idHerramienta" required class="w-full px-3 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-sm focus:ring-2 focus:ring-indigo-500 outline-none dark:text-white">
                  <option value="0" disabled>Seleccione una herramienta</option>
                  <option v-for="herr in herramientas" :key="herr.idHerramienta" :value="herr.idHerramienta">
                    {{ herr.nombre }} (Stock: {{ herr.stock }})
                  </option>
                </select>
              </div>

              <!-- Motivo -->
              <div class="md:col-span-2">
                <label class="block text-xs font-bold text-gray-500 uppercase mb-1">Motivo / Destino *</label>
                <input v-model="form.motivo" type="text" required class="w-full px-3 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-sm focus:ring-2 focus:ring-indigo-500 outline-none dark:text-white" placeholder="Ej: Trabajo externo, Reparación en campo..." />
              </div>

              <!-- Cantidad -->
              <div>
                <label class="block text-xs font-bold text-gray-500 uppercase mb-1">Cantidad Salida *</label>
                <input v-model.number="form.cantidadSalida" type="number" min="1" required class="w-full px-3 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-sm focus:ring-2 focus:ring-indigo-500 outline-none dark:text-white" placeholder="1" />
              </div>

              <!-- Fechas -->
              <div>
                <label class="block text-xs font-bold text-gray-500 uppercase mb-1">Fecha Salida *</label>
                <input v-model="form.fechaSalida" type="datetime-local" required class="w-full px-3 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-sm focus:ring-2 focus:ring-indigo-500 outline-none dark:text-white" />
              </div>

              <!-- Usuarios -->
              <div>
                <label class="block text-xs font-bold text-gray-500 uppercase mb-1">Entregado Por *</label>
                <select v-model="form.idUsuarioEntrega" required class="w-full px-3 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-sm focus:ring-2 focus:ring-indigo-500 outline-none dark:text-white">
                  <option value="" disabled>Seleccione usuario</option>
                  <option v-for="user in usuarios" :key="user.idUsuario" :value="user.idUsuario">{{ user.nombreUsuario || user.usuario }}</option>
                </select>
              </div>
              <div>
                <label class="block text-xs font-bold text-gray-500 uppercase mb-1">Técnico Responsable *</label>
                <select v-model="form.idUsuarioRecibe" required class="w-full px-3 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-sm focus:ring-2 focus:ring-indigo-500 outline-none dark:text-white">
                  <option value="" disabled>Seleccione técnico</option>
                  <option v-for="user in usuarios" :key="user.idUsuario" :value="user.idUsuario">{{ user.nombreUsuario || user.usuario }}</option>
                </select>
              </div>

              <!-- Observaciones -->
              <div class="md:col-span-2">
                <label class="block text-xs font-bold text-gray-500 uppercase mb-1">Observaciones adicionales</label>
                <textarea v-model="form.observaciones" rows="2" class="w-full px-3 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-sm focus:ring-2 focus:ring-indigo-500 outline-none dark:text-white resize-none" placeholder="Detalles extra..."></textarea>
              </div>
            </div>

            <div class="mt-6 flex gap-3">
              <button type="button" @click="closeModal" class="flex-1 py-2.5 bg-gray-100 text-gray-600 rounded-xl font-bold text-sm hover:bg-gray-200 transition-all uppercase tracking-wider">
                Cancelar
              </button>
              <button type="submit" :disabled="saving" class="flex-1 py-2.5 bg-indigo-600 text-white rounded-xl font-bold text-sm hover:bg-indigo-700 transition-all shadow-lg shadow-indigo-200 dark:shadow-none uppercase tracking-wider flex items-center justify-center gap-2">
                <Icon v-if="saving" icon="svg-spinners:ring-resize" />
                {{ saving ? 'Guardando...' : (isEditing ? 'Actualizar' : 'Registrar') }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </Teleport>

    <!-- Modal Detalle -->
    <Teleport to="body">
      <div v-if="showDetailModal" class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
        <div class="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-fadeIn">
          <div class="p-4 border-b border-gray-100 dark:border-gray-700 flex justify-between items-center bg-gray-50 dark:bg-gray-700">
            <h2 class="font-bold text-gray-800 dark:text-white">Detalle de Salida: {{ selectedSalida.codigoSalida }}</h2>
            <button @click="showDetailModal = false"><Icon icon="mdi:close" /></button>
          </div>
          <div class="p-6 space-y-4">
            <div class="flex justify-between border-b pb-2 dark:border-gray-700">
              <span class="text-xs text-gray-500 font-bold uppercase">Estado:</span>
              <span :class="['px-2 py-0.5 rounded-full text-[10px] font-bold uppercase', getEstadoClass(selectedSalida.estado)]">{{ selectedSalida.estado }}</span>
            </div>
            <div class="flex justify-between border-b pb-2 dark:border-gray-700">
              <span class="text-xs text-gray-500 font-bold uppercase">Herramienta:</span>
              <span class="text-sm font-bold text-indigo-600 dark:text-indigo-400">{{ selectedSalida.nombreHerramienta }}</span>
            </div>
            <div class="flex justify-between border-b pb-2 dark:border-gray-700">
              <span class="text-xs text-gray-500 font-bold uppercase">Motivo:</span>
              <span class="text-xs dark:text-gray-300">{{ selectedSalida.motivo || '---' }}</span>
            </div>
            <div class="flex justify-between border-b pb-2 dark:border-gray-700">
              <span class="text-xs text-gray-500 font-bold uppercase">Cantidad:</span>
              <span class="text-sm font-bold dark:text-white">{{ selectedSalida.cantidadSalida }}</span>
            </div>
            <div class="flex justify-between border-b pb-2 dark:border-gray-700">
              <span class="text-xs text-gray-500 font-bold uppercase">Fecha Salida:</span>
              <span class="text-xs dark:text-gray-300">{{ formatDate(selectedSalida.fechaSalida) }} {{ formatTime(selectedSalida.fechaSalida) }}</span>
            </div>
            <div class="flex justify-between border-b pb-2 dark:border-gray-700">
              <span class="text-xs text-gray-500 font-bold uppercase">Entregado Por:</span>
              <span class="text-xs font-bold dark:text-gray-200">{{ getUsuarioNombre(selectedSalida.idUsuarioEntrega) }}</span>
            </div>
            <div class="flex justify-between border-b pb-2 dark:border-gray-700">
              <span class="text-xs text-gray-500 font-bold uppercase">Técnico Responsable:</span>
              <span class="text-xs font-bold dark:text-gray-200">{{ getUsuarioNombre(selectedSalida.idUsuarioRecibe) }}</span>
            </div>
            <div v-if="selectedSalida.fechaDevolucionReal" class="flex justify-between border-b pb-2 dark:border-gray-700">
              <span class="text-xs text-gray-500 font-bold uppercase">Devolución Real:</span>
              <span class="text-xs dark:text-gray-300">{{ formatDate(selectedSalida.fechaDevolucionReal) }}</span>
            </div>
            <div>
              <span class="text-xs text-gray-500 font-bold uppercase block mb-1">Observaciones:</span>
              <p class="text-xs bg-gray-50 dark:bg-gray-700 p-2 rounded-lg dark:text-gray-200 min-h-[60px]">
                {{ selectedSalida.observaciones || 'Sin observaciones' }}
              </p>
            </div>
          </div>
          <div class="p-4 bg-gray-50 dark:bg-gray-700 flex justify-end gap-2">
            <button v-if="selectedSalida.estado !== 'Devuelto'" @click="$router.push(`/devolucion-herramienta/nueva/${selectedSalida.idSalida}`)" class="px-4 py-2 bg-green-600 text-white rounded-lg text-xs font-bold hover:bg-green-700 transition-colors">Ir a Devolución</button>
            <button @click="showDetailModal = false" class="px-4 py-2 bg-indigo-600 text-white rounded-lg text-xs font-bold">Cerrar</button>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script>
import { Icon } from "@iconify/vue";
import { useToast } from "vue-toastification";
import { 
  obtenerSalidasHerramienta, 
  crearSalidaHerramienta, 
  actualizarSalidaHerramienta 
} from "@/api/salidaHerramienta";
import { getHerramientas } from "@/api/herramientas";
import { obtenerUsuarios } from "@/api/usuarios";
import { getCurrentUser } from "@/api/index";

export default {
  name: "SalidaHerramientaList",
  components: { Icon },
  setup() {
    const toast = useToast();
    return { toast };
  },
  data() {
    return {
      salidas: [],
      usuarios: [],
      herramientas: [],
      loading: false,
      saving: false,
      showModal: false,
      showDetailModal: false,
      isEditing: false,
      selectedSalida: null,
      
      // Filtros
      filterEstado: 'all',
      searchQuery: '',
      
      // Paginación
      currentPage: 1,
      itemsPerPage: 10,
      
      // Formulario
      form: {
        idSalida: 0,
        codigoSalida: "",
        idHerramienta: 0,
        cantidadSalida: 1,
        fechaSalida: this.getLocalDateString(),
        fechaDevolucionReal: null,
        estado: "Pendiente",
        motivo: "",
        observaciones: "",
        idUsuarioEntrega: "",
        idUsuarioRecibe: ""
      }
    };
  },
  computed: {
    filteredSalidas() {
      let filtered = [...this.salidas];
      
      if (this.filterEstado !== 'all') {
        filtered = filtered.filter(s => s.estado === this.filterEstado);
      }
      
      if (this.searchQuery.trim()) {
        const query = this.searchQuery.toLowerCase();
        filtered = filtered.filter(s => 
          s.codigoSalida.toLowerCase().includes(query) || 
          (s.nombreHerramienta && s.nombreHerramienta.toLowerCase().includes(query)) ||
          (s.motivo && s.motivo.toLowerCase().includes(query))
        );
      }
      
      return filtered.sort((a, b) => new Date(b.fechaSalida) - new Date(a.fechaSalida));
    },
    totalPages() {
      return Math.ceil(this.filteredSalidas.length / this.itemsPerPage);
    },
    paginatedSalidas() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      return this.filteredSalidas.slice(start, start + this.itemsPerPage);
    }
  },
  async mounted() {
    await this.loadData();
    await this.fetchHerramientas();
    await this.fetchUsuarios();
  },
  methods: {
    getLocalDateString() {
      const now = new Date();
      now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
      return now.toISOString().slice(0, 16);
    },
    async loadData() {
      this.loading = true;
      try {
        const res = await obtenerSalidasHerramienta();
        if (res.success) {
          this.salidas = res.data || [];
        } else {
          this.toast.error("Error al cargar salidas: " + res.error);
        }
      } catch (error) {
        this.toast.error("Error de conexión");
      } finally {
        this.loading = false;
      }
    },
    async fetchUsuarios() {
      try {
        const res = await obtenerUsuarios();
        if (res.success) {
          this.usuarios = res.data || [];
          
          // Set delivery user as current user by default if creating
          if (!this.isEditing && !this.form.idUsuarioEntrega) {
            const currentUser = getCurrentUser();
            if (currentUser) {
              this.form.idUsuarioEntrega = currentUser.idUsuario;
            }
          }
        }
      } catch (error) {
        console.error("Error fetching usuarios:", error);
      }
    },
    async fetchHerramientas() {
      try {
        const res = await getHerramientas();
        if (res.success) this.herramientas = res.data || [];
      } catch (error) {
        console.error("Error fetching herramientas:", error);
      }
    },
    getUsuarioNombre(id) {
      const user = this.usuarios.find(u => u.idUsuario === id);
      return user ? (user.nombreUsuario || user.usuario) : `ID: ${id}`;
    },
    getEstadoClass(estado) {
      switch (estado) {
        case 'Pendiente': return 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-400';
        case 'Devuelto': return 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400';
        case 'Atrasado': return 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400';
        default: return 'bg-gray-100 text-gray-700 dark:bg-gray-900/40 dark:text-gray-400';
      }
    },
    formatDate(date) {
      if (!date) return "";
      return new Date(date).toLocaleDateString();
    },
    formatTime(date) {
      if (!date) return "";
      return new Date(date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    },
    handleFilterChange() {
      this.currentPage = 1;
    },
    resetFilters() {
      this.filterEstado = 'all';
      this.searchQuery = '';
      this.currentPage = 1;
    },
    openCreateModal() {
      this.isEditing = false;
      this.resetForm();
      this.showModal = true;
    },
    openEditModal(salida) {
      this.isEditing = true;
      this.selectedSalida = salida;
      this.form = { ...salida };
      // Format date for datetime-local input
      if (this.form.fechaSalida) {
        const d = new Date(this.form.fechaSalida);
        d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
        this.form.fechaSalida = d.toISOString().slice(0, 16);
      }
      this.showModal = true;
    },
    closeModal() {
      this.showModal = false;
    },
    resetForm() {
      const currentUser = getCurrentUser();
      this.form = {
        idSalida: 0,
        codigoSalida: "",
        idHerramienta: 0,
        cantidadSalida: 1,
        fechaSalida: this.getLocalDateString(),
        fechaDevolucionReal: null,
        estado: "Pendiente",
        motivo: "",
        observaciones: "",
        idUsuarioEntrega: currentUser ? currentUser.idUsuario : "",
        idUsuarioRecibe: ""
      };
    },
    async saveSalida() {
      if (!this.form.codigoSalida || !this.form.idUsuarioEntrega || !this.form.idUsuarioRecibe || !this.form.idHerramienta) {
        this.toast.warning("Por favor complete los campos obligatorios");
        return;
      }

      this.saving = true;
      try {
        let res;
        // Clean DTO for backend
        const payload = { ...this.form };
        
        if (this.isEditing) {
          res = await actualizarSalidaHerramienta(this.form.idSalida, payload);
        } else {
          res = await crearSalidaHerramienta(payload);
        }

        if (res.success) {
          this.toast.success(`Salida ${this.isEditing ? 'actualizada' : 'registrada'} con éxito`);
          this.closeModal();
          await this.loadData();
        } else {
          this.toast.error("Error: " + res.error);
        }
      } catch (error) {
        this.toast.error("Error al guardar");
      } finally {
        this.saving = false;
      }
    },
    viewDetails(salida) {
      this.selectedSalida = salida;
      this.showDetailModal = true;
    }
  }
};
</script>

<style scoped>
.animate-fadeIn {
  animation: fadeIn 0.3s ease-out;
}
@keyframes fadeIn {
  from { opacity: 0; transform: scale(0.95); }
  to { opacity: 1; transform: scale(1); }
}
</style>
